<template>
  <section id="analysis-unlock-panel" class="unlock-panel">
    <v-card class="unlock-card card-action">
      <v-card-item class="pb-2">
        <div class="d-flex align-center mb-3">
          <v-icon icon="mdi-lock-open-outline" color="primary" class="mr-2" />
          <span class="text-overline">{{ $t('analysis.unlock.eyebrow') }}</span>
        </div>
        <h2 class="text-h5 font-weight-bold mb-2">
          {{ offer.headline }}
        </h2>
      </v-card-item>

      <v-card-text>
        <div
          v-if="teaserPhase === 'preparing'"
          class="teaser-shell teaser-shell--preparing mb-4"
        >
          <div class="teaser-placeholder" aria-hidden="true">
            <div class="teaser-placeholder-content">
              <span class="teaser-pill">{{ t('analysis.unlock.teaser.loadingHint') }}</span>
            </div>
          </div>
          <div class="teaser-progress">
            <div
              v-for="step in localizedTeaserSteps"
              :key="step.key"
              class="teaser-step"
              :class="`is-${step.status}`"
            >
              <span class="teaser-step-indicator"></span>
              <span>{{ step.label }}</span>
            </div>
          </div>
        </div>

        <div
          v-else-if="teaserPhase === 'ready'"
          class="teaser-shell teaser-shell--ready mb-4"
        >
          <div class="teaser-ready-badge">
            {{ t('analysis.unlock.teaser.readyBadge') }}
          </div>
          <img :src="teaserImageSrc" alt="" class="teaser-image" />
          <p class="teaser-caption text-body-2 mb-0">{{ teaserCaption }}</p>
        </div>

        <div
          v-else-if="teaserPhase === 'fallback'"
          class="teaser-shell teaser-shell--fallback mb-4"
        >
          <p class="text-body-2 mb-0">{{ t('analysis.unlock.teaser.fallback') }}</p>
        </div>

        <div class="text-subtitle-2 font-weight-bold mb-3">
          {{ offer.includedLabel }}
        </div>

        <p
          v-if="offer.expectationsQuote"
          class="unlock-context text-body-2 text-medium-emphasis mb-4"
        >
          <span class="font-weight-medium">{{ t('analysis.unlock.contextLabel') }}:</span>
          <em>{{ offer.expectationsQuote }}</em>
        </p>

        <ul class="unlock-list">
          <li class="font-weight-medium">{{ offer.primaryReason }}</li>
          <li v-for="bullet in offer.bullets" :key="bullet">{{ bullet }}</li>
        </ul>

        <v-btn
          color="primary"
          size="large"
          class="mt-4 unlock-cta"
          @click="$emit('unlock')"
        >
          {{ ctaLabel }}
        </v-btn>
      </v-card-text>
    </v-card>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';
import { buildUnlockOffer } from '@/utils/unlockOffer.mjs';
import { canStartNewPremiumAnalysis } from '@/utils/currentAnalysisAccess.mjs';

const props = defineProps({
  userIntent: {
    type: Object,
    default: null,
  },
  teaserPhase: {
    type: String,
    default: 'hidden',
  },
  teaserSteps: {
    type: Array,
    default: () => [],
  },
  teaserImageSrc: {
    type: String,
    default: '',
  },
  teaserCaption: {
    type: String,
    default: '',
  },
});

defineEmits(['unlock']);

const { t } = useI18n();
const authStore = useAuthStore();
const hasCreditsAvailable = computed(() =>
  canStartNewPremiumAnalysis({ credits: authStore.credits }),
);

const offer = computed(() =>
  buildUnlockOffer(props.userIntent, {
    genericHeadline: hasCreditsAvailable.value
      ? t('analysis.unlock.readyTitle')
      : t('analysis.unlock.title'),
    headlineTemplate: (purpose) =>
      hasCreditsAvailable.value
        ? t('analysis.unlock.readyPersonalizedTitle', { purpose })
        : t('analysis.unlock.personalizedTitle', { purpose }),
    genericReason: hasCreditsAvailable.value
      ? t('analysis.unlock.readyDescription')
      : t('analysis.unlock.description'),
    includedLabel: hasCreditsAvailable.value
      ? t('analysis.unlock.readyLabel')
      : t('analysis.unlock.includedLabel'),
    bullets: hasCreditsAvailable.value
      ? [
          t('analysis.unlock.readyReasons.requirements'),
          t('analysis.unlock.readyReasons.premiumSections'),
          t('analysis.unlock.readyReasons.roomAndCost'),
        ]
      : [
          t('analysis.unlock.reasons.requirements'),
          t('analysis.unlock.reasons.premiumSections'),
          t('analysis.unlock.reasons.roomAndCost'),
        ],
    purposeLabels: {
      personal: t('analysis.unlock.purposes.personal'),
      professional: t('analysis.unlock.purposes.professional'),
      client: t('analysis.unlock.purposes.client'),
      other: t('analysis.unlock.purposes.other'),
    },
  })
);

const ctaLabel = computed(() =>
  hasCreditsAvailable.value
    ? t('analysis.unlock.readyCta')
    : t('analysis.unlock.cta'),
);

const teaserStepLabels = {
  goals: 'analysis.unlock.teaser.steps.goals',
  recommendations: 'analysis.unlock.teaser.steps.recommendations',
  preview: 'analysis.unlock.teaser.steps.preview',
};

const localizedTeaserSteps = computed(() =>
  props.teaserSteps.map((step) => ({
    ...step,
    label: t(teaserStepLabels[step.key] || teaserStepLabels.preview),
  })),
);
</script>

<style scoped>
.unlock-panel {
  scroll-margin-top: 96px;
}

.unlock-context {
  color: var(--text-secondary);
}

.teaser-shell {
  border-radius: 16px;
  border: 1px solid rgba(var(--v-theme-primary), 0.14);
  overflow: hidden;
  background: linear-gradient(180deg, rgba(var(--v-theme-primary), 0.06), rgba(var(--v-theme-primary), 0.03));
  box-shadow: 0 10px 24px rgba(16, 24, 40, 0.08);
}

.teaser-shell--preparing {
  display: grid;
  gap: 0;
}

.teaser-placeholder {
  min-height: 184px;
  background:
    repeating-linear-gradient(
      0deg,
      rgba(255, 255, 255, 0.18) 0 2px,
      transparent 2px 30px
    ),
    repeating-linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.12) 0 2px,
      transparent 2px 34px
    ),
    radial-gradient(circle at top left, rgba(var(--v-theme-primary), 0.3), transparent 50%),
    linear-gradient(135deg, rgba(var(--v-theme-primary), 0.16), rgba(var(--v-theme-primary), 0.06));
  position: relative;
}

.teaser-placeholder::before {
  content: '';
  position: absolute;
  top: 0;
  left: -30%;
  width: 30%;
  height: 100%;
  background: linear-gradient(100deg, transparent, rgba(255, 255, 255, 0.55), transparent);
  animation: teaser-scan 2.2s infinite ease-in-out;
}

.teaser-placeholder::after {
  content: '';
  position: absolute;
  inset: 0;
  backdrop-filter: blur(14px);
}

.teaser-placeholder-content {
  position: absolute;
  left: 14px;
  bottom: 14px;
  z-index: 1;
}

.teaser-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: var(--font-size-caption);
  letter-spacing: 0.01em;
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.84);
  border: 1px solid rgba(15, 23, 42, 0.08);
}

.teaser-progress {
  display: grid;
  gap: 10px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.42);
  backdrop-filter: blur(8px);
}

.teaser-step {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-secondary);
}

.teaser-step-indicator {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: rgba(var(--v-theme-primary), 0.18);
  flex: 0 0 auto;
}

.teaser-step.is-active {
  color: var(--text-primary);
  font-weight: var(--font-weight-medium);
}

.teaser-step.is-active .teaser-step-indicator {
  background: rgb(var(--v-theme-primary));
  box-shadow: 0 0 0 6px rgba(var(--v-theme-primary), 0.12);
}

.teaser-step.is-done .teaser-step-indicator {
  background: rgb(var(--v-theme-primary));
}

.teaser-shell--ready {
  position: relative;
}

.teaser-ready-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 1;
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 999px;
  padding: 5px 10px;
  backdrop-filter: blur(8px);
}

.teaser-image {
  display: block;
  width: 100%;
  min-height: 184px;
  max-height: 300px;
  object-fit: cover;
  filter: blur(14px) saturate(0.9);
  transform: scale(1.04);
  transition: filter 0.35s ease, transform 0.35s ease;
}

.teaser-caption {
  padding: 14px 16px;
  color: var(--text-secondary);
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(8px);
}

.teaser-shell--fallback {
  padding: 16px;
}

.unlock-list {
  margin: 0;
  padding-left: 1.25rem;
  color: var(--text-primary);
}

.unlock-list li {
  margin-bottom: 0.75rem;
}

.unlock-list li:last-child {
  margin-bottom: 0;
}

.unlock-cta {
  max-width: 100%;
  min-height: 44px;
  white-space: normal;
}

.unlock-cta :deep(.v-btn__content) {
  min-width: 0;
  overflow: visible;
  text-align: center;
  text-overflow: unset;
  white-space: normal;
  line-height: 1.2;
}

@media (max-width: 600px) {
  .unlock-cta {
    width: 100%;
    height: auto !important;
    padding: 10px 12px;
  }
}

@keyframes teaser-scan {
  from {
    left: -35%;
  }

  to {
    left: 105%;
  }
}
</style>
